import {
  Typography,
  Box,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getStatusColor } from "@/Components/helpers/util";
import { useState, useMemo, useRef, useEffect } from "react";
import { ContextMenu } from "./ContextMenu";
import { JsonViewer } from "./JsonViewer";
import {
  generateCurlCommand,
  generateFetchCommand,
  generatePowershellCommand,
  copyToClipboard,
} from "../../../../utils/captureNetwork";

export const NetworkRequestList = ({
  expandedRequest,
  // networkContainerRef,
  expandedView,
  setExpandedView,
  setExpandedRequest,
  filteredNetworkRequests,
  // startResize,
  // networkSplitPosition,
  filterText,
  handleRequestClick,
  // isDragging,
  activeRequestId,
  recordingStartTime = 0,
  onSeekToTime,
}: {
  expandedRequest: string | null;
  expandedView: string;
  setExpandedView: (view: string) => void;
  setExpandedRequest: (id: string | null) => void;
  filteredNetworkRequests: any[];
  filterText: string;
  handleRequestClick: (id: string) => void;
  activeRequestId?: string | null;
  recordingStartTime?: number;
  onSeekToTime?: (timeInSeconds: number) => void;
}) => {
  const [networkSplitPosition, setNetworkSplitPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const localContainerRef = useRef<HTMLDivElement>(null);
  const activeRowRef = useRef<HTMLDivElement>(null);
  const lastScrolledId = useRef<string | null>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [contextMenu, setContextMenu] = useState<{
    position: { top: number; left: number };
    request: any;
  } | null>(null);
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event bubbling
    setIsDragging(true);
  };
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!localContainerRef.current) return;

      const containerRect = localContainerRef.current.getBoundingClientRect();
      const relativeX = e.clientX - containerRect.left;
      const newPercent = (relativeX / containerRect.width) * 100;

      // Clamp between 20% and 80%
      setNetworkSplitPosition(Math.max(20, Math.min(80, newPercent)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging]);

  // Auto-scroll to active request when it changes
  useEffect(() => {
    if (!activeRequestId || activeRequestId === lastScrolledId.current) return;
    if (!activeRowRef.current || !listContainerRef.current) return;

    // Only scroll if this is a different request
    lastScrolledId.current = activeRequestId;

    // Scroll the active row into view
    activeRowRef.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [activeRequestId]);

  const handleContextMenu = (event: React.MouseEvent, request: any) => {
    event.preventDefault();
    setContextMenu({
      position: { top: event.clientY, left: event.clientX },
      request: request,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleCopySuccess = (message: string) => {
    setSnackbar({ open: true, message, severity: "success" });
    handleCloseContextMenu();
  };

  const handleCopyError = () => {
    setSnackbar({
      open: true,
      message: "Failed to copy to clipboard",
      severity: "error",
    });
    handleCloseContextMenu();
  };

  const handleCopyCurl = async () => {
    if (!contextMenu) return;
    const curlCommand = generateCurlCommand(contextMenu.request);
    const success = await copyToClipboard(curlCommand);
    success ? handleCopySuccess("Copied as cURL!") : handleCopyError();
  };

  const handleCopyFetch = async () => {
    if (!contextMenu) return;
    const fetchCommand = generateFetchCommand(contextMenu.request);
    const success = await copyToClipboard(fetchCommand);
    success ? handleCopySuccess("Copied as Fetch!") : handleCopyError();
  };

  const handleCopyPowershell = async () => {
    if (!contextMenu) return;
    const psCommand = generatePowershellCommand(contextMenu.request);
    const success = await copyToClipboard(psCommand);
    success ? handleCopySuccess("Copied as PowerShell!") : handleCopyError();
  };

  const handleCopyUrl = async () => {
    if (!contextMenu) return;
    const success = await copyToClipboard(contextMenu.request.fullUrl);
    success ? handleCopySuccess("URL copied!") : handleCopyError();
  };

  // ========================================================================
  // CONTENT VIEWER (Robust JSON Parsing)
  // ========================================================================
  const ContentViewer = ({
    content,
    title,
  }: {
    content: any;
    title: string;
  }) => {
    // Helper to recursively parse deeply stringified JSON
    const { parsedData, isValidJson } = useMemo(() => {
      if (!content || content === "[No payload]") {
        return { parsedData: null, isValidJson: false };
      }

      // 1. If it's already an object/array, use it directly
      if (typeof content === "object" && content !== null) {
        return { parsedData: content, isValidJson: true };
      }

      // 2. If it's a string, try to parse it recursively
      if (typeof content === "string") {
        try {
          // Remove BOM and trim whitespace
          let cleaned = content.replace(/^\uFEFF/, "").trim();
          let parsed = cleaned;
          let iterations = 0;
          const maxIterations = 5; // Prevent infinite loops

          // Keep parsing until we get an object or reach max iterations
          while (typeof parsed === "string" && iterations < maxIterations) {
            try {
              const temp = JSON.parse(parsed);
              parsed = temp;
              iterations++;
            } catch {
              // If parsing fails, we've reached the final string
              break;
            }
          }

          // Check if we ended up with a valid object
          if (typeof parsed === "object" && parsed !== null) {
            return { parsedData: parsed, isValidJson: true };
          }

          // If after all parsing attempts it's still a string, return as plain text
          return { parsedData: parsed, isValidJson: false };
        } catch (e) {
          return { parsedData: content, isValidJson: false };
        }
      }

      return { parsedData: content, isValidJson: false };
    }, [content]);

    if (!content || content === "[No payload]") {
      return (
        <Typography sx={{ p: 2, color: "#666", fontSize: "0.75rem" }}>
          No {title.toLowerCase()} data available.
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 1,
            borderBottom: "1px solid #e0e0e0",
            bgcolor: "#f9f9f9",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontSize: "0.75rem", fontWeight: "bold" }}
          >
            {title}
          </Typography>
          <Tooltip title="Copy Content">
            <IconButton
              size="small"
              onClick={async () => {
                const textToCopy = isValidJson
                  ? JSON.stringify(parsedData, null, 2)
                  : String(content);
                const success = await copyToClipboard(textToCopy);
                setSnackbar({
                  open: true,
                  message: success ? "Content copied!" : "Failed to copy",
                  severity: success ? "success" : "error",
                });
              }}
            >
              <ContentCopyIcon sx={{ fontSize: "0.8rem" }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 1.5,
            bgcolor: "#fff",
          }}
        >
          {isValidJson ? (
            <JsonViewer data={parsedData} maxExpandLevel={2} />
          ) : (
            <Box
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                color: "#333",
              }}
            >
              {String(content)}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // ========================================================================

  return (
    <>
      <Box
        ref={localContainerRef}
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          fontSize: "0.75rem",
          height: "100%",
          width: "100%",
        }}
      >
        {/* Request List - Left side */}
        <Box
          sx={{
            width: expandedRequest ? `${networkSplitPosition}%` : "100%",
            overflow: "auto",
            transition: isDragging ? "none" : "width 0.1s ease",
            display: "flex",
            flexDirection: "column",
            minWidth: "20%",
          }}
        >
          {/* Table Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "30px 1fr 60px 60px 100px 60px",
              borderBottom: "1px solid #e0e0e0",
              position: "sticky",
              top: 0,
              bgcolor: "#f5f5f5",
              zIndex: 2,
              minWidth: "400px",
            }}
          >
            {["", "Name", "Method", "Status", "Domain", "Time"].map(
              (header, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1,
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    color: "#555",
                    borderRight: idx < 5 ? "1px solid #e0e0e0" : "none",
                    bgcolor: "#f5f5f5",
                  }}
                >
                  {header}
                </Box>
              )
            )}
          </Box>

          {/* Table Rows */}
          <Box ref={listContainerRef} sx={{ flex: 1 }}>
            {filteredNetworkRequests.length === 0 ? (
              <Typography
                sx={{
                  p: 2,
                  color: "#666",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                No network requests found.{" "}
                {filterText && "Try changing your filter."}
              </Typography>
            ) : (
              filteredNetworkRequests.map((request: any, index: number) => {
                const isActive = activeRequestId === request.id;
                return (
                <Box
                  key={request.id || index}
                  ref={isActive ? activeRowRef : null}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "30px 1fr 60px 60px 100px 60px",
                    borderBottom: "1px solid #f0f0f0",
                    borderLeft: isActive ? "3px solid #673ab7" : "3px solid transparent",
                    bgcolor: isActive
                      ? "rgba(103, 58, 183, 0.15)"
                      : expandedRequest === request.id
                        ? "#e3f2fd"
                        : index % 2 === 0
                        ? "#fff"
                        : "#fafafa",
                    "&:hover": { bgcolor: isActive ? "rgba(103, 58, 183, 0.2)" : "#f1f8ff" },
                    cursor: "pointer",
                    minWidth: "400px",
                    alignItems: "center",
                    transition: "background-color 0.2s ease, border-left-color 0.2s ease",
                  }}
                  onClick={() => {
                    handleRequestClick(request.id);
                    // Seek video to this request's timestamp
                    if (onSeekToTime) {
                      let reqTimeMs = request.startTime;
                      if (!reqTimeMs && request.timestamp) {
                        reqTimeMs = typeof request.timestamp === 'number'
                          ? request.timestamp
                          : new Date(request.timestamp).getTime();
                      }
                      if (reqTimeMs && !isNaN(reqTimeMs)) {
                        const relativeTimeSec = (reqTimeMs - recordingStartTime) / 1000;
                        if (relativeTimeSec >= 0) {
                          onSeekToTime(relativeTimeSec);
                        }
                      }
                    }
                  }}
                  onContextMenu={(e) => handleContextMenu(e, request)}
                >
                  <Box sx={{ p: 0.75, textAlign: "center", color: "#666" }}>
                    {index + 1}
                  </Box>

                  <Box
                    sx={{
                      p: 0.75,
                      overflow: "hidden",
                      borderRight: "1px solid #f0f0f0",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "#333",
                        fontWeight:
                          expandedRequest === request.id ? "bold" : "normal",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={request.fullUrl}
                    >
                      {request.name || "Unknown"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: "#888",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {request.fullUrl?.split("?")[0]}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 0.75, borderRight: "1px solid #f0f0f0" }}>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: "bold",
                        color:
                          request.method === "GET"
                            ? "#2196f3"
                            : request.method === "POST"
                            ? "#4caf50"
                            : request.method === "DELETE"
                            ? "#f44336"
                            : "#ff9800",
                      }}
                    >
                      {request.method}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 0.75, borderRight: "1px solid #f0f0f0" }}>
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 0.5,
                        borderRadius: 1,
                        bgcolor: request.status >= 400 ? "#ffebee" : "#e8f5e9",
                        color: getStatusColor(request.status),
                      }}
                    >
                      {request.status === 0 ? "Pending" : request.status}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 0.75,
                      overflow: "hidden",
                      borderRight: "1px solid #f0f0f0",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#555",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                      title={request.domain}
                    >
                      {request.domain}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 0.75, textAlign: "right", pr: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color:
                          parseInt(request.duration || request.time) > 1000
                            ? "#f44336"
                            : "#555",
                      }}
                    >
                      {request.duration
                        ? `${Math.round(request.duration)}ms`
                        : request.time}
                    </Typography>
                  </Box>
                </Box>
              );
              })
            )}
          </Box>
        </Box>

        {/* Resize Handle */}
        {expandedRequest && (
          <Box
            className={`resizer ${isDragging ? "dragging" : ""}`}
            onMouseDown={startResize}
            sx={{
              width: "4px",
              cursor: "col-resize",
              height: "100%",
              backgroundColor: isDragging ? "#2196f3" : "#e0e0e0",
              zIndex: 10,
              transition: "background-color 0.2s",
              "&:hover": { backgroundColor: "#2196f3" },
              ...(isDragging && { backgroundColor: "#2196f3" }),
            }}
          />
        )}

        {/* Request Details - Right side */}
        {expandedRequest && (
          <Box
            sx={{
              width: `${100 - networkSplitPosition}%`,
              borderLeft: "1px solid #e0e0e0",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
              position: "relative",
              minWidth: "20%",
            }}
          >
            {(() => {
              const selectedRequest = filteredNetworkRequests.find(
                (req: any) => req.id === expandedRequest
              );
              if (!selectedRequest) return null;

              return (
                <>
                  {/* Detail Header */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderBottom: "1px solid #e0e0e0",
                      bgcolor: "#f9f9f9",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          color: "#333",
                          wordBreak: "break-all",
                          pr: 4,
                        }}
                      >
                        {selectedRequest.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedRequest(null);
                        }}
                        sx={{ mt: -0.5, mr: -0.5 }}
                      >
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        display: "block",
                        wordBreak: "break-all",
                        mt: 0.5,
                        mb: 1,
                        fontFamily: "monospace",
                      }}
                    >
                      {selectedRequest.fullUrl}
                      <Tooltip title="Copy URL">
                        <IconButton
                          size="small"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const success = await copyToClipboard(
                              selectedRequest.fullUrl
                            );
                            setSnackbar({
                              open: true,
                              message: success
                                ? "URL copied!"
                                : "Failed to copy",
                              severity: success ? "success" : "error",
                            });
                          }}
                          sx={{ ml: 0.5, p: 0.2 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: "0.7rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Typography>

                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Tabs
                        value={expandedView}
                        onChange={(e, newValue) => setExpandedView(newValue)}
                        variant="standard"
                        sx={{
                          minHeight: "32px",
                          "& .MuiTab-root": {
                            minHeight: "32px",
                            fontSize: "0.75rem",
                            textTransform: "none",
                            py: 0.5,
                          },
                        }}
                      >
                        <Tab label="Headers" value="Headers" />
                        <Tab label="Payload" value="Request" />
                        <Tab label="Response" value="Response" />
                      </Tabs>
                    </Box>
                  </Box>

                  {/* Detail Content Area */}
                  <Box sx={{ flex: 1, overflow: "auto", p: 0 }}>
                    {/* HEADERS VIEW */}
                    {expandedView === "Headers" && (
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            fontSize: "0.75rem",
                          }}
                        >
                          General
                        </Typography>
                        <Box
                          sx={{ pl: 1.5, mb: 2, borderLeft: "2px solid #eee" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            <strong>Request URL:</strong>{" "}
                            {selectedRequest.fullUrl}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            <strong>Request Method:</strong>{" "}
                            {selectedRequest.method}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            <strong>Status Code:</strong>{" "}
                            {selectedRequest.status}
                          </Typography>
                        </Box>

                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            fontSize: "0.75rem",
                          }}
                        >
                          Response Headers
                        </Typography>
                        <Box
                          sx={{ pl: 1.5, mb: 2, borderLeft: "2px solid #eee" }}
                        >
                          {selectedRequest.headers?.response ? (
                            Object.entries(
                              selectedRequest.headers.response
                            ).map(([key, value]) => (
                              <Typography
                                key={key}
                                variant="body2"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontFamily: "monospace",
                                  mb: 0.5,
                                }}
                              >
                                <strong>{key}:</strong> {String(value)}
                              </Typography>
                            ))
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{ fontStyle: "italic" }}
                            >
                              No response headers
                            </Typography>
                          )}
                        </Box>

                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            fontSize: "0.75rem",
                          }}
                        >
                          Request Headers
                        </Typography>
                        <Box sx={{ pl: 1.5, borderLeft: "2px solid #eee" }}>
                          {selectedRequest.headers?.request ? (
                            Object.entries(selectedRequest.headers.request).map(
                              ([key, value]) => (
                                <Typography
                                  key={key}
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.75rem",
                                    fontFamily: "monospace",
                                    mb: 0.5,
                                  }}
                                >
                                  <strong>{key}:</strong> {String(value)}
                                </Typography>
                              )
                            )
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{ fontStyle: "italic" }}
                            >
                              No request headers
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* PAYLOAD VIEW */}
                    {expandedView === "Request" && (
                      <ContentViewer
                        content={selectedRequest.requestPayload}
                        title="Request Payload"
                      />
                    )}

                    {/* RESPONSE VIEW */}
                    {expandedView === "Response" && (
                      <ContentViewer
                        content={selectedRequest.responseContent}
                        title="Response Body"
                      />
                    )}
                  </Box>
                </>
              );
            })()}
          </Box>
        )}
      </Box>

      {/* Context Menu */}
      <ContextMenu
        anchorPosition={contextMenu?.position || null}
        onClose={handleCloseContextMenu}
        onCopyCurl={handleCopyCurl}
        onCopyFetch={handleCopyFetch}
        onCopyPowershell={handleCopyPowershell}
        onCopyUrl={handleCopyUrl}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
